import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { startCase } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import isEmpty from 'lodash.isempty';
import ProductCard from './ProductCard';
import config from '../../config';
import { formatMoney } from '../../util/helpers/numeralHelpers';
import { selectAlgoliaImageRootUrl } from '../../store/selectors/settingsSelectors';
import { parseEnumType } from '../../util/helpers/enumMappers';
import { productLocationTypes } from '../../util/enum/api/productTypes';
import { HorizontalLine } from '../HorizontalLine/HorizontalLine';

import useStepPost from '../../hooks/useStepPost';
import { useMetalStreamData } from '../../hooks/useMetalStreamData';
import { metalStreamDataHelper } from '../../util/helpers/metalStreamDataHelper';
import useMetalStreamProductDispatch from '../../hooks/useMetalStreamProductDispatch';
import { getApplication } from '../../store/actions/application/applicationActions';
import useApplicationUid from '../../hooks/useApplicationUid';
import { ACCOUNT_STEP_COMPLETE } from '../../util/gtmEvents';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';

const ProductListStyles = styled.div`
  display: flex;
  flex-direction: column;

  > :last-child {
    display: none;
  }
`;

const ProductsList = ({
  products,
  nextPath,
  skipStepPath,
  productType,
  onModalClose,
}) => {
  const dispatch = useDispatch();
  const { ApplicationUid } = useApplicationUid();
  const RootUrl = useSelector(selectAlgoliaImageRootUrl);
  const { MetalStreamData } = useMetalStreamData();
  const { gtmStepEvent } = useGtmStepHook();
  const goldProductCode = metalStreamDataHelper(
    MetalStreamData,
    'SymbolCodeGold',
  );
  const silverProductCode = metalStreamDataHelper(
    MetalStreamData,
    'SymbolCodeSilver',
  );

  const goldPercentage = metalStreamDataHelper(
    MetalStreamData,
    'GoldPercentage',
  );

  const silverPercentage = metalStreamDataHelper(
    MetalStreamData,
    'SilverPercentage',
  );

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel:
        productType === 'Gold'
          ? 'MetalStream choose gold'
          : 'MetalStream choose silver',
    });
  };

  const { onSuccess, onSkipStep } = useStepPost({
    nextPath,
    skipStepPath,
    onModalClose,
  });
  const { metalStreamProductDispatch } = useMetalStreamProductDispatch(
    productType,
  );
  const onGetApplicationSuccess = (application) => {
    if (isEmpty(application)) {
      return;
    }
    onSuccess();
  };
  const onAPISuccess = () => {
    dispatch(
      getApplication({
        data: { ApplicationUid },
        onSuccess: onGetApplicationSuccess,
      }),
    );
    handleGtmEvent();
  };
  const selectProduct = (symbolCode) => {
    metalStreamProductDispatch({
      data: {
        MetalType: productType,
        ProductSymbolCode: symbolCode,
      },
      onSuccess: onAPISuccess,
    });
  };

  useEffect(() => {
    if (!goldPercentage && productType === 'Gold') {
      onSkipStep();
    }
  }, [goldPercentage]); // eslint-disable-line

  useEffect(() => {
    if (!silverPercentage && productType === 'Silver') {
      onSkipStep();
    }
  }, [silverPercentage]); // eslint-disable-line
  return (
    <ProductListStyles>
      {products.map((product) => {
        const imgSrc = config.algoliaSearch.imageCode
          .replace('{product_code}', product.ProductCode)
          .replace('{base_url}', RootUrl);

        return product.Locations.map(
          ({ LocationType, PricePerOz, PricePerUnit, SymbolCode }) => {
            const ProductName = `${product.ProductCaption} - ${startCase(
              parseEnumType(productLocationTypes, LocationType),
            )}`;

            const pricePerOz = formatMoney(PricePerOz);
            const pricePerUnit = formatMoney(PricePerUnit);
            const ProductPrice = `${pricePerOz}/oz; ${pricePerUnit} each`;

            return (
              <>
                <ProductCard
                  key={ProductName}
                  imgSrc={imgSrc}
                  product={{
                    ...product,
                  }}
                  isSelected={
                    SymbolCode === goldProductCode ||
                    SymbolCode === silverProductCode
                  }
                  symbolCode={SymbolCode}
                  productPrice={ProductPrice}
                  productName={ProductName}
                  onClick={() => {
                    selectProduct(SymbolCode);
                  }}
                />
                <HorizontalLine />
              </>
            );
          },
        );
      })}
    </ProductListStyles>
  );
};

ProductsList.propTypes = {
  products: PropTypes.arrayOf(PropTypes.shape({})),
  nextPath: PropTypes.string,
  skipStepPath: PropTypes.string,
  productType: PropTypes.string,
  onModalClose: PropTypes.func,
};

export default ProductsList;
